import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  computed,
  input,
  signal,
  viewChild,
} from '@angular/core';
import {
  CdkDragDrop,
  DragDropModule,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { UiFormComponent } from '@template-ui/components';
import { EzFormInputType, InputConfig } from '@template-ui/types/ez-form.types';
import {
  FormLabelResolver,
  GeneratedFormDefinition,
  GeneratedFormField,
  GeneratedFormValues,
  FormIssues,
  generatedInputConfig,
  visibleGeneratedFields,
} from '@template-ui/helpers/generated-form.helpers';
import { TerminologyFormIdentityDirective, TerminologyFormControlUsageMap } from '../terminology/terminology-form-identity.directive';

/** Presentation only: its owning command adapter supplies identities and submits. */
@Component({
  selector: 'phms-generated-command-form',
  standalone: true,
  imports: [CommonModule, UiFormComponent, DragDropModule, TerminologyFormIdentityDirective],
  template: `@if (authoring) {
      <div
        class="authored-fields"
        cdkDropList
        cdkDropListOrientation="mixed"
        [cdkDropListData]="definition().fields"
        [cdkDropListDisabled]="!reorderable"
        (cdkDropListDropped)="reorder($event)"
        aria-label="Form fields"
      >
        <ng-template #authoringField let-field>
          <div
            class="authored-field"
            cdkDrag
            [cdkDragData]="field.name"
            [cdkDragDisabled]="!reorderable"
            [class.authored-field--selected]="selectedField === field.name"
            [class.authored-field--invalid]="!!authoringIssues[field.name]"
          >
            @if (reorderable) {
              <button
                type="button"
                class="field-grip"
                cdkDragHandle
                [attr.aria-label]="'Move ' + fieldLabel(field.name)"
                title="Drag to move field"
                (click)="select(field.name)"
              >
                <i class="pi pi-bars" aria-hidden="true"></i>
              </button>
            }
            <button
              type="button"
              class="field-select"
              [attr.aria-label]="'Configure ' + fieldLabel(field.name)"
              [attr.aria-pressed]="selectedField === field.name"
              (click)="select(field.name)"
            >
              <span class="field-heading"
                ><span
                  >{{ fieldLabel(field.name)
                  }}<span *ngIf="field.required" aria-label="Required">
                    *</span
                  ></span
                >
                <span class="field-badges"
                  ><span
                    *ngIf="field.visibleWhen || field.requiredWhen"
                    class="field-condition"
                    >Conditional</span
                  ><i class="pi pi-pencil" aria-hidden="true"></i
                ></span>
              </span>
              <span
                class="field-sample"
                [class.field-sample--textarea]="field.control === 'textarea'"
                [class.field-sample--checkbox]="field.control === 'checkbox'"
                aria-hidden="true"
              >
                <i
                  *ngIf="field.control === 'checkbox'"
                  class="pi"
                  [class.pi-check-square]="values[field.name] === true"
                  [class.pi-stop]="values[field.name] !== true"
                ></i>
                <span>{{ sample(field) }}</span>
                <i
                  *ngIf="field.control === 'select'"
                  class="pi pi-chevron-down"
                ></i>
                <i
                  *ngIf="
                    field.control === 'date' || field.control === 'date-time' || field.control === 'datetime'
                  "
                  class="pi pi-calendar"
                ></i>
              </span>
              <span *ngIf="authoringIssues[field.name]" class="field-error"
                ><i class="pi pi-exclamation-circle" aria-hidden="true"></i>
                {{ authoringIssues[field.name] }}</span
              >
            </button>
          </div>
        </ng-template>
        <ui-form
          [config]="authoringConfig()"
          [asColumn]="false"
          [stackOnNarrow]="definition().stackOnNarrow ?? false"
          [fillRemainingRowSpace]="true"
          [useAutoSave]="false"
        />
      </div>
    } @else {
      <ui-form
        [appTerminologyFormIdentity]="terminologyControls()"
        [config]="config()"
        [formValues]="values"
        (formValuesChange)="changed($event)"
        [disabled]="disabled"
        [asColumn]="false"
        [stackOnNarrow]="definition().stackOnNarrow ?? false"
        [fillRemainingRowSpace]="true"
        [useAutoSave]="false"
      />
      <ul *ngIf="issueEntries.length" role="alert" aria-label="Form validation">
        <li *ngFor="let issue of issueEntries">
          {{ fieldLabel(issue[0]) }}: {{ issue[1] }}
        </li>
      </ul>
    }`,
  styles: [
    `
      :host {
        display: block;
        min-width: 0;
        max-width: 100%;
      }
      ul {
        color: var(--custom-text-color-error);
        margin: 0.5rem 0;
        padding-left: 1.25rem;
      }
      .authored-fields {
        min-width: 0;
      }
      .authored-field {
        display: flex;
        width: 100%;
        align-items: stretch;
        min-width: 0;
        border: 1px solid transparent;
        border-radius: 5px;
        background: var(--custom-surface-panel-color);
        color: var(--custom-text-strong-color);
      }
      .authored-field:hover {
        border-color: var(--custom-interactive-selected-border-color);
      }
      .authored-field--selected {
        background: var(--custom-interactive-selected-background-color);
        color: var(--custom-interactive-selected-text-color);
        border-color: var(--custom-interactive-selected-border-color);
        box-shadow: 0 0 0 1px var(--custom-interactive-selected-border-color);
      }
      .authored-field--invalid {
        border-left: 3px solid var(--custom-text-color-error);
      }
      button {
        color: inherit;
        font: inherit;
      }
      .field-select {
        display: grid;
        gap: 0.3rem;
        flex: 1;
        min-width: 0;
        min-height: 44px;
        padding: 0.45rem;
        background: transparent;
        border: 0;
        text-align: left;
        cursor: pointer;
        border-radius: 4px;
      }
      .field-select:focus-visible,
      .field-grip:focus-visible {
        outline: 2px solid var(--custom-focus-ring-color);
        outline-offset: 2px;
      }
      .field-heading,
      .field-badges {
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }
      .field-heading {
        justify-content: space-between;
        font-weight: 600;
      }
      .field-heading > span:first-child {
        min-width: 0;
        overflow-wrap: anywhere;
      }
      .field-badges {
        font-size: 0.72rem;
        font-weight: 400;
      }
      .field-condition {
        border: 1px solid var(--custom-border-subtle-color);
        border-radius: 3px;
        padding: 0.05rem 0.3rem;
      }
      .field-sample {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.5rem;
        border: 1px solid var(--custom-input-normal-border-color);
        background: var(--custom-input-background-color);
        color: var(--custom-input-normal-text-color);
        border-radius: 4px;
        padding: 0.55rem 0.6rem;
        min-height: 2.25rem;
        font-weight: 400;
        overflow-wrap: anywhere;
      }
      .field-sample--textarea {
        min-height: 4rem;
        align-items: flex-start;
      }
      .field-sample--checkbox {
        border: 0;
        background: transparent;
        color: inherit;
        justify-content: flex-start;
        padding-left: 0;
      }
      .field-grip {
        align-self: stretch;
        flex: 0 0 44px;
        padding: 0.25rem;
        border: 0;
        background: transparent;
        cursor: grab;
        opacity: 0.65;
      }
      .field-grip:active {
        cursor: grabbing;
      }
      .field-error {
        color: var(--custom-text-color-error);
        font-size: 0.78rem;
      }
      .cdk-drag-preview {
        box-sizing: border-box;
        box-shadow: var(--custom-shadow-elevation-2);
        color: var(--custom-text-strong-color);
      }
      .cdk-drag-placeholder {
        opacity: 0.25;
      }
    `,
  ],
})
export class GeneratedCommandFormComponent {
  readonly definition = input.required<GeneratedFormDefinition>();
  readonly label = input<FormLabelResolver>((_, fallback) => fallback);
  readonly terminologyControls = input<TerminologyFormControlUsageMap>({});
  private readonly inputValues = signal<GeneratedFormValues>({});
  private readonly authoringField = viewChild<TemplateRef<unknown>>('authoringField');
  private readonly authoringTemplate = computed(() => this.authoringField() ?? null);
  /** Authoring samples use UiForm's existing width, row and responsive layout. */
  readonly authoringConfig = computed<InputConfig[]>(() =>
    generatedInputConfig(this.definition(), this.label()).map((control, index) => ({
      name: control.name,
      type: EzFormInputType.COMPONENT,
      width: control.width,
      row: control.row,
      template: this.authoringTemplate,
      templateContext: { $implicit: this.definition().fields[index] },
    })),
  );
  @Input() set values(value: GeneratedFormValues) {
    this.inputValues.set(value ?? {});
  }
  get values(): GeneratedFormValues {
    return this.inputValues();
  }
  @Input() issues: FormIssues = {};
  @Input() disabled = false;
  @Input() authoring = false;
  @Input() selectedField: string | null = null;
  @Input() authoringIssues: Record<string, string> = {};
  @Input() reorderable = false;
  @Output() readonly fieldConfigure = new EventEmitter<string>();
  @Output() readonly fieldOrderChange = new EventEmitter<string[]>();
  @Output() readonly valuesChange = new EventEmitter<GeneratedFormValues>();
  private readonly visibleNames = computed(() =>
    JSON.stringify(
      visibleGeneratedFields(this.definition(), this.inputValues()).map(
        (field) => field.name,
      ),
    ),
  );
  private readonly selectedChoiceValues = computed(() => JSON.stringify(Object.fromEntries(
    this.definition().fields.filter(field => field.control === 'select').map(field => [field.name, this.inputValues()[field.name]]),
  )));
  readonly config = computed(() => {
    const visible = new Set<string>(JSON.parse(this.visibleNames()));
    return generatedInputConfig(
      {
        ...this.definition(),
        fields: this.definition().fields.filter((field) =>
          visible.has(field.name),
        ),
      },
      this.label(),
      {},
      JSON.parse(this.selectedChoiceValues()),
    );
  });
  get issueEntries(): [string, string][] {
    return Object.entries(this.issues);
  }
  fieldLabel(name: string): string {
    const field = this.definition().fields.find((f) => f.name === name);
    return field ? this.label()(field.labelToken, field.label) || name : name;
  }
  sample(field: GeneratedFormField): string {
    const value = this.values[field.name];
    if (value !== undefined && value !== null && value !== '')
      return (
        field.options?.find((option) => option.value === value)?.label ??
        String(value)
      );
    if (field.control === 'checkbox') return 'Not selected';
    if (field.control === 'select') return 'Select…';
    if (field.control === 'date') return 'mm/dd/yyyy';
    return '';
  }
  select(name: string): void {
    if (
      this.authoring &&
      this.definition().fields.some((field) => field.name === name)
    )
      this.fieldConfigure.emit(name);
  }
  reorder(event: CdkDragDrop<readonly GeneratedFormField[]>): void {
    const names = this.definition().fields.map((field) => field.name);
    if (
      !this.authoring ||
      !this.reorderable ||
      event.previousContainer !== event.container ||
      !event.isPointerOverContainer ||
      !Number.isInteger(event.previousIndex) ||
      !Number.isInteger(event.currentIndex) ||
      event.previousIndex < 0 ||
      event.currentIndex < 0 ||
      event.previousIndex >= names.length ||
      event.currentIndex >= names.length ||
      event.previousIndex === event.currentIndex ||
      event.item && event.item.data !== names[event.previousIndex] ||
      new Set(names).size !== names.length
    )
      return;
    moveItemInArray(names, event.previousIndex, event.currentIndex);
    this.fieldOrderChange.emit(names);
  }
  changed(value: GeneratedFormValues): void {
    if (!this.authoring) this.valuesChange.emit(value);
  }
}
